/**
 * 施工実績カテゴリ詳細ページ
 */

import React from "react";
import "./ResultCategoryDetailPage.css";

interface ResultCategoryDetailPageProps {
  category: any;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onBack: () => void;
}

// 日付をフォーマット（曜日付きJST）
function formatDateTimeJST(value: any): string {
  if (!value) return "-";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    const weekdays = ["日", "月", "火", "水", "木", "金", "土"];
    const options: Intl.DateTimeFormatOptions = {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    const formatted = date.toLocaleString("ja-JP", options);
    const jstDate = new Date(
      date.toLocaleString("en-US", { timeZone: "Asia/Tokyo" })
    );
    const weekday = weekdays[jstDate.getDay()];
    return formatted.replace(/日\s/, `日(${weekday}) `);
  } catch {
    return String(value);
  }
}

export const ResultCategoryDetailPage: React.FC<ResultCategoryDetailPageProps> = ({
  category,
  onEdit,
  onDelete,
  onBack,
}) => {
  if (!category) {
    return (
      <div className="result-category-detail-page">
        <div className="empty-state">
          <p>カテゴリが見つかりません</p>
          <button className="btn btn-primary" onClick={onBack}>
            一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  const thumbnailUrl = category.thumbnailUrl || category.thumbnail_url;

  return (
    <div className="result-category-detail-page">
      {/* ヘッダー */}
      <div className="result-category-detail-header">
        <h1>カテゴリ詳細</h1>
        <div className="result-category-detail-actions">
          <button className="btn btn-outline" onClick={onBack}>
            一覧に戻る
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => onEdit(category.id)}
          >
            編集
          </button>
          <button
            className="btn btn-danger"
            onClick={() => onDelete(category.id)}
          >
            削除
          </button>
        </div>
      </div>

      <div className="result-category-detail-content">
        {/* サムネイル画像セクション */}
        {thumbnailUrl && (
          <section className="detail-section">
            <h2 className="section-title">サムネイル画像</h2>
            <div className="thumbnail-container">
              <img src={thumbnailUrl} alt={category.name} className="thumbnail-image" />
            </div>
          </section>
        )}

        {/* 基本情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">基本情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>ID:</label>
              <span>{category.id}</span>
            </div>
            <div className="detail-item">
              <label>カテゴリ名:</label>
              <span>{category.name}</span>
            </div>
            <div className="detail-item">
              <label>表示順序:</label>
              <span>{category.displayOrder ?? category.display_order ?? 0}</span>
            </div>
            <div className="detail-item">
              <label>状態:</label>
              <span
                className={`status-badge ${
                  category.isEnabled ?? category.is_enabled
                    ? "enabled"
                    : "disabled"
                }`}
              >
                {category.isEnabled ?? category.is_enabled ? "有効" : "無効"}
              </span>
            </div>
            {(category.description || "") && (
              <div className="detail-item full-width">
                <label>説明:</label>
                <span>{category.description}</span>
              </div>
            )}
          </div>
        </section>

        {/* システム情報セクション */}
        <section className="detail-section">
          <h2 className="section-title">システム情報</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>作成日時:</label>
              <span>
                {formatDateTimeJST(category.createDate || category.create_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>更新日時:</label>
              <span>
                {formatDateTimeJST(category.updateDate || category.update_date)}
              </span>
            </div>
            <div className="detail-item">
              <label>バージョン:</label>
              <span>{category.version || "-"}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
